import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { ProgressBar } from '@/components/ProgressBar'
import {
  MODE_LABEL,
  OUTCOMES,
  plannedWrites,
  type Example,
  type Outcome,
  type RunDetail,
  type RunStatus,
} from '@/lib/fieldCopy'
import { fmtTimestamp } from '@/lib/http'
import { BlockTitle } from '@/pages/Tge/TgeReports'

const STATUS_TONE: Record<RunStatus, string> = {
  planning: 'text-text-2',
  planned: 'text-foreground',
  applying: 'text-warn',
  applied: 'text-ok',
  paused: 'text-warn',
  cancelled: 'text-warn',
  failed: 'text-danger',
  reverting: 'text-warn',
  reverted: 'text-text-2',
}

const OUTCOME_TONE: Record<Outcome, string> = {
  write: 'text-ok',
  equal: 'text-text-2',
  target_filled: 'text-warn',
  source_empty: 'text-text-2',
  unconvertible: 'text-danger',
}

/** Run status as a word in its status colour. */
export function RunStatusBadge({ status }: { status: RunStatus }) {
  return <span className={`font-mono text-[12px] font-medium ${STATUS_TONE[status] ?? 'text-text-2'}`}>{status}</span>
}

function progressOf(run: RunDetail): { value: number; label: string } | null {
  const n = (k: string) => run.by_status[k] ?? 0
  if (run.status === 'applying') {
    const total = n('planned') + n('applied') + n('failed') + n('uncertain') + n('drifted')
    const done = total - n('planned')
    return { value: total ? (done / total) * 100 : 0, label: `writing — ${done} of ${total} rows` }
  }
  if (run.status === 'reverting') {
    const total = n('applied') + n('uncertain') + n('reverted')
    return {
      value: total ? (n('reverted') / total) * 100 : 0,
      label: `reverting — ${n('reverted')} of ${total} rows`,
    }
  }
  return null
}

type ExampleRow = Example & { id: string; outcome: Outcome; pair: string }

const exampleColumns = (multiPair: boolean): Column<ExampleRow>[] => [
  { key: 'issue_key', header: 'Issue', mono: true, width: '120px' },
  ...(multiPair ? [{ key: 'pair', header: 'Pair', width: '22%' } as Column<ExampleRow>] : []),
  {
    key: 'outcome', header: 'Outcome', width: '130px',
    render: (e) => <span className={`font-mono text-[12px] font-medium ${OUTCOME_TONE[e.outcome]}`}>{e.outcome}</span>,
    title: (e) => e.outcome,
  },
  { key: 'source', header: 'Source', render: (e) => e.source || '—' },
  { key: 'target', header: 'Target now', render: (e) => e.target || '—' },
  {
    key: 'result', header: 'Will write / reason',
    render: (e) => (e.outcome === 'write' ? e.new ?? '—' : e.reason ?? '—'),
    title: (e) => (e.outcome === 'write' ? e.new ?? undefined : e.reason ?? undefined),
  },
]

/** Outcome chips: a radio group that filters the example table. */
function OutcomeChips({ counts, value, onChange }: {
  counts: [string, number][]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div role="group" aria-label="Filter examples by outcome" className="flex flex-wrap gap-1.5">
      {counts.map(([k, n]) => {
        const on = value === k
        return (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(k)}
            className={[
              'rounded-full border px-2.5 py-0.5 text-[11.5px] transition-colors',
              on ? 'border-primary bg-surface-active text-foreground' : 'border-line-strong text-text-2 hover:text-foreground',
            ].join(' ')}
          >
            {k} <span className="font-mono">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

/** Report of one run: progress, outcome counters per pair, Automation warning, examples. */
export function RunReport({ run }: { run: RunDetail }) {
  const [filter, setFilter] = useState<string>('all')
  const counters = run.counters ?? {}
  const pairCounters = counters.pairs ?? []
  const progress = progressOf(run)
  const rules = counters.automation_rules ?? []
  const warnings = counters.warnings ?? []
  const statusEntries = Object.entries(run.by_status).sort(([a], [b]) => a.localeCompare(b))
  const writes = plannedWrites(counters)
  const multiPair = run.pairs.length > 1

  const examples: ExampleRow[] = pairCounters.flatMap((c, i) => {
    const p = run.pairs[i]
    const pair = p ? `${p.source_name} → ${p.target_name}` : `pair ${i + 1}`
    return OUTCOMES.flatMap((o) => (c.examples?.[o] ?? []).map((e) => ({ ...e, outcome: o, pair, id: `${i}|${o}|${e.issue_key}` })))
  })
  const outcomeCounts = OUTCOMES
    .map((o) => [o, examples.filter((e) => e.outcome === o).length] as [string, number])
    .filter(([, n]) => n > 0)
  const shown = filter === 'all' ? examples : examples.filter((e) => e.outcome === filter)

  return (
    <div className="space-y-5 px-4 py-3">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-[14px] font-semibold text-foreground">Run #{run.id}</h2>
          <RunStatusBadge status={run.status} />
          <span className="font-mono text-[11.5px] text-text-3">
            {run.actor} · {fmtTimestamp(run.created_at)} · {run.issues_total} issues read · {writes} writes planned
          </span>
        </div>
        <p className="mt-1 break-words font-mono text-[12px] text-text-2">{run.jql}</p>
        {run.error && <p className="mt-1 font-mono text-[12px] text-danger">{run.error}</p>}
      </div>

      {run.status === 'planning' && <ProgressBar indeterminate label={`planning — ${run.issues_total} issues read`} />}
      {progress && <ProgressBar value={progress.value} label={progress.label} />}

      {statusEntries.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Rows by status">
          {statusEntries.map(([k, v]) => (
            <span key={k} className="rounded bg-surface px-1.5 py-0.5 font-mono text-[11.5px] text-text-2">
              {`${k} ${v}`}
            </span>
          ))}
        </div>
      )}

      <section>
        <BlockTitle>Outcomes per pair</BlockTitle>
        <ul className="border-t border-line">
          {run.pairs.map((p, i) => {
            const c = pairCounters[i]
            return (
              <li key={`${p.source}-${p.target}`} className="border-b border-line-row py-2">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-[13px] text-foreground">{p.source_name} → {p.target_name}</span>
                  <span className="font-mono text-[11.5px] text-text-3">
                    {p.source_kind} → {p.target_kind} · {MODE_LABEL[p.mode]}
                  </span>
                </div>
                <dl className="mt-1 flex flex-wrap gap-x-5 gap-y-1">
                  {OUTCOMES.map((o) => (
                    <div key={o} className="flex items-baseline gap-1.5">
                      <dt className={`font-mono text-[11.5px] ${OUTCOME_TONE[o]}`}>{o}</dt>
                      <dd className="font-mono text-[13px] text-foreground">{c?.outcomes[o] ?? 0}</dd>
                    </div>
                  ))}
                  <div className="flex items-baseline gap-1.5">
                    <dt className="font-mono text-[11.5px] text-text-3">bulk / single</dt>
                    <dd className="font-mono text-[13px] text-foreground">{c?.bulk ?? 0} / {c?.single ?? 0}</dd>
                  </div>
                </dl>
              </li>
            )
          })}
        </ul>
        {counters.estimate && (
          <p className="mt-2 font-mono text-[11.5px] text-text-3">
            {counters.estimate.bulk} rows via bulk ({counters.estimate.bulk_groups} groups) ·{' '}
            {counters.estimate.single} one by one · notifications off
          </p>
        )}
      </section>

      {rules.length > 0 && (
        <section className="border-l-2 border-warn pl-3">
          <div className="flex items-center gap-2 text-[13px] font-medium text-foreground">
            <AlertTriangle className="size-4 shrink-0 text-warn" aria-hidden />
            Automation rules that react to the target field
          </div>
          <p className="mt-1 text-[12px] text-text-2">
            Every written issue can trigger them, and REST cannot pause a rule — disable them in Jira
            for the duration if their runs matter.
          </p>
          <ul className="mt-2 space-y-1">
            {rules.map((r) => (
              <li key={`${r.target}-${r.rule_uuid}`} className="font-mono text-[12px] text-text-2">
                {r.name ?? r.rule_uuid} · {r.where.join(', ')} · {r.state ?? '—'} · {r.target}
              </li>
            ))}
          </ul>
        </section>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 font-mono text-[12px] text-text-2">
              <AlertTriangle className="mt-0.5 size-3 shrink-0 text-warn" aria-hidden />
              {w}
            </li>
          ))}
        </ul>
      )}

      {examples.length > 0 && (
        <section>
          <BlockTitle aside={<OutcomeChips counts={[['all', examples.length], ...outcomeCounts]} value={filter} onChange={setFilter} />}>
            Examples
          </BlockTitle>
          <div className="border-t border-line">
            <DataTable
              label="Example issues"
              rows={shown}
              columns={exampleColumns(multiPair)}
              rowId={(e) => e.id}
              empty="No examples for this outcome."
            />
          </div>
        </section>
      )}
    </div>
  )
}
